import { Activity, ChevronDown, ChevronUp, ExternalLink, Pin, PinOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

export type ActivityLevel = "I" | "W" | "E";

export interface ActivityEvent {
  ts: string;
  node?: string;
  tx: React.ReactNode;
  lvl?: ActivityLevel;
  current?: boolean;
  pending?: boolean;
}

interface ActivityDrawerProps {
  events: ActivityEvent[];
  pinned: boolean;
  setPinned: (next: boolean | ((prev: boolean) => boolean)) => void;
  collapsed: boolean;
  setCollapsed: (next: boolean | ((prev: boolean) => boolean)) => void;
  height?: number;
  elapsed?: string;
}

export function ActivityDrawer({
  events,
  pinned,
  setPinned,
  collapsed,
  setCollapsed,
  height = 220,
  elapsed,
}: ActivityDrawerProps) {
  const { t } = useTranslation();
  return (
    <div
      className={cn(
        "mt-auto sticky bottom-0 z-30 flex-shrink-0 border-t border-[#e8ebf0] bg-[#0d1b2e] text-white flex flex-col font-mono overflow-hidden transition-[height] duration-200 ease-out",
        pinned && "shadow-[0_-4px_16px_rgba(8,37,76,0.1)]",
      )}
      style={{ height: collapsed ? 32 : height }}
    >
      {/* A barra NÃO é um botão. Era, e os três controles de ação viviam dentro
          dele, com um `stopPropagation` contornando a consequência: `<button>`
          dentro de `<button>` é HTML inválido, o React avisa a cada render, e
          navegação por teclado e leitor de tela não alcançam o controle
          aninhado. Agora o toggle e as ações são irmãos, e o toggle ocupa o
          espaço livre para a área de clique continuar a mesma. */}
      <div className="h-8 flex items-center px-3.5 gap-2.5 border-b border-white/10 text-[10px] text-white/85 tracking-[0.14em] uppercase justify-between flex-shrink-0 select-none">
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
          aria-controls="sku-activity-drawer-body"
          className="flex flex-1 items-center gap-2.5 bg-transparent border-0 p-0 text-inherit font-inherit tracking-inherit uppercase cursor-pointer text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#ffc83d]"
        >
          <div className="flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className="w-1.5 h-1.5 rounded-full bg-[#5fcc86] mg-pulse motion-reduce:animate-none"
            style={{ boxShadow: "0 0 6px #5fcc86" }}
          />
          <Activity className="w-3 h-3" />
          <span>{t("Live Activity · SSE Stream")}</span>
          <span className="text-white/40 normal-case tracking-[0.04em] ml-1">
            · {t("{{count}} events", { count: events.length })}
            {elapsed ? ` · ${t("{{elapsed}} elapsed", { elapsed })}` : ""}
          </span>
        </div>
        </button>
        {/* Sem `stopPropagation`: com o toggle virando irmão, não há mais
            clique do pai para conter. */}
        <div className="flex gap-1">
          <DrawerActionButton
            on={pinned}
            onClick={() => setPinned((p) => !p)}
            title={pinned ? t("Unpin drawer") : t("Pin drawer open")}
            aria-label={pinned ? t("Unpin drawer") : t("Pin drawer open")}
          >
            {pinned ? <PinOff className="w-2.5 h-2.5" /> : <Pin className="w-2.5 h-2.5" />}
          </DrawerActionButton>
          <DrawerActionButton title={t("Open in panel")} aria-label={t("Open in panel")}>
            <ExternalLink className="w-2.5 h-2.5" />
          </DrawerActionButton>
          <DrawerActionButton
            onClick={() => setCollapsed((c) => !c)}
            title={collapsed ? t("Expand drawer") : t("Collapse drawer")}
            aria-label={collapsed ? t("Expand drawer") : t("Collapse drawer")}
          >
            {collapsed ? <ChevronUp className="w-2.5 h-2.5" /> : <ChevronDown className="w-2.5 h-2.5" />}
          </DrawerActionButton>
        </div>
      </div>
      {!collapsed && (
        <div
          id="sku-activity-drawer-body"
          className="flex-1 overflow-auto px-3.5 py-2 text-[10.5px] leading-relaxed min-h-0"
        >
          {events.length === 0 && (
            <div className="text-white/40 normal-case tracking-[0.02em] py-1">
              {t("0 events · waiting for SSE stream…")}
            </div>
          )}
          {events.map((a, i) => {
            const indicator = a.current
              ? "▶"
              : a.pending
                ? "○"
                : a.lvl === "E"
                  ? "✗"
                  : a.lvl === "W"
                    ? "!"
                    : "✓";
            return (
              <div
                key={i}
                className={cn(
                  "grid grid-cols-[75px_28px_1fr] gap-2 py-px",
                  a.current && "[&_.tx]:text-[#ffc83d]",
                  a.pending && "[&_.tx]:text-white/40",
                  a.lvl === "E" && "[&_.tx]:text-[#ff9d9d]",
                )}
              >
                <span className="text-white/35">[{a.ts}]</span>
                <span
                  className={cn(
                    "text-[#ffc83d] font-bold text-[9px] tracking-[0.1em]",
                    a.lvl === "W" && "text-[#ffc83d]",
                    a.lvl === "E" && "text-[#ff7575]",
                  )}
                >
                  {a.node ? `N${a.node}` : (a.lvl ?? "I")}
                </span>
                <span className="tx text-white/85 break-words">
                  {indicator} {a.tx}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface DrawerActionButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  on?: boolean;
}

function DrawerActionButton({ on, className, children, ...rest }: DrawerActionButtonProps) {
  return (
    <button
      type="button"
      className={cn(
        "w-6 h-[22px] inline-flex items-center justify-center bg-transparent border border-white/10 rounded text-white/65 hover:bg-white/10 hover:text-white transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#ffc83d]",
        on && "bg-[rgba(255,200,61,0.15)] border-[rgba(255,200,61,0.4)] text-[#ffc83d]",
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}
